import React, { useState, ReactElement, ChangeEvent } from 'react';
import MailCheck from 'react-mailcheck';
import { getTextColor } from '../style';
import TextInputV2 from './TextInputV2';
import { EMAIL_ALLOWED_DOMAINS, EMAIL_REGEX } from '../config/constants';
import { EVENT_NAMES } from '../constants/mixpanel';
import { useApp } from '../contexts/AppContext';
import { isEmpty } from '../utils/helpers';
import { handleNewJoinFlowVysionEvent } from '../utils/vysion/join/newJoinFlow';
import { NewJoinFlowClickVysionEventsEnum } from '../utils/vysion/join/newJoinFlow/click';
import { IEmailInputProps } from './types/EmailInput';
import { mixpanelTrackEvent, mixpanelTrackEventAnonymously } from '../utils/mixpanel/common';
import { AutoSuggestList, AutoSuggestListItem, EmailAutoSuggestWrapper } from './EmailInputStyles';
import colors from '../theme/colors';

const checkEmail = (email: string | undefined | null) => {
  if (typeof email !== 'string') {
    return false;
  }

  if (email.includes('@z.incube8.sg')) {
    return true;
  }

  return EMAIL_REGEX.test(email);
};

const EmailInputV2 = ({
  onChange,
  value: val,
  inputLabel,
  statusMessage,
  status,
  customStyle,
  isLogin = false,
  autoFocus,
  customInputStyle,
  autoSuggestStyle,
  customStyleLabelContainer,
  setHasValidEmail,
  onError,
  ...rest
}: IEmailInputProps): ReactElement => {
  const [fieldError, setFieldError] = useState('');
  const [emailSuggestion, setEmailSuggestion] = useState('');
  const [suggestedDomains, setSuggestedDomains] = useState<string[]>([]);
  const [showDomainSuggestion, setShowDomainSuggestion] = useState(false);
  const [isFocused, setIsFocused] = useState(false);

  const [cursor, setCursor] = useState(-1);
  const { accountHashId } = useApp();
  const pathname = window.location.pathname;

  const getEmailStatusMessage = (suggestion: { full: string }) => {
    if (suggestion && checkEmail(suggestion.full)) {
      return (
        <div style={{ color: 'black' }}>
          Did you mean{' '}
          <span
            style={{ cursor: 'pointer', color: getTextColor().BRAND.color, fontWeight: 600 }}
            onClick={() => {
              setEmailSuggestion('');
              setOnChangeEmail(suggestion.full);
            }}
          >
            {suggestion.full}
          </span>
          ?
        </div>
      );
    }
  };

  const setOnChangeEmail = (value: string) => {
    // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
    onChange({ target: { id: 'email', value } });

    if (value?.length > 0 || checkEmail(value)) {
      setHasValidEmail && setHasValidEmail(false);
    }
  };

  const getDomainSuggestions = (actualValue: string, slicedValue: string) => {
    const strValue = slicedValue.trim().toLowerCase();
    const strLength = strValue.length;
    const allowedDomains = EMAIL_ALLOWED_DOMAINS;

    if (strLength === 0) {
      return [];
    }

    const domainSuggestions = allowedDomains.filter(domain => {
      return domain.split(' ').some(text => {
        return text.toLowerCase().slice(0, strLength) === strValue;
      });
    });

    if (domainSuggestions.length === 1 && domainSuggestions[0] === strValue) {
      return [];
    }

    return domainSuggestions.map(domains => actualValue + domains);
  };

  const handleEmailAutoSuggest = (e: React.ChangeEvent<HTMLInputElement>, isFocused = false) => {
    const { value } = e.target;
    const slicedValue = value.slice(value.indexOf('@'));
    const actualValue = value.slice(0, value.indexOf('@'));
    const suggestedDomainList = getDomainSuggestions(actualValue, slicedValue);

    setCursor(-1);
    setShowDomainSuggestion(!isEmpty(actualValue) && suggestedDomainList.length > 0);
    setSuggestedDomains(suggestedDomainList);
    setOnChangeEmail(value);
    setIsFocused(isFocused);
  };

  const handleOnClickVysionEvent = () => {
    pathname === '/join' &&
      handleNewJoinFlowVysionEvent({
        event: NewJoinFlowClickVysionEventsEnum.EMAIL_ENTRY,
      });
  };

  const setSelectedDomain = (value: string) => {
    setOnChangeEmail(value);
    const domain = value?.slice(value.indexOf('@'));

    if (!domain) return;

    pathname === '/join' &&
      handleNewJoinFlowVysionEvent({
        event: NewJoinFlowClickVysionEventsEnum.EMAIL_DOMAIN_SELECTION,
      });

    if (accountHashId) {
      mixpanelTrackEvent({
        eventName: EVENT_NAMES.USER_CLICKED_EMAIL_DOMAIN,
        payload: {
          eventProperties: {
            Domain: domain,
          },
        },
      });
    } else {
      mixpanelTrackEventAnonymously({
        eventName: EVENT_NAMES.USER_CLICKED_EMAIL_DOMAIN,
        payload: {
          eventProperties: { Domain: domain },
        },
      });
    }
  };

  const handleOnTextChange = (e: ChangeEvent<HTMLInputElement>, suggestion: { full: string }) => {
    const { value } = e.target;
    let error = '';
    const emailMessage = getEmailStatusMessage(suggestion);

    if (typeof value === 'string' && value.length > 0 && checkEmail(value) === false) {
      error = 'Please use a valid email address.';
    }

    // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
    onChange({ target: { id: 'email', value } }, !!(error || emailMessage));

    setFieldError(error);
    setShowDomainSuggestion(false);
    // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
    setEmailSuggestion(emailMessage ? emailMessage : '');
    setIsFocused(false);
    setHasValidEmail && setHasValidEmail(isEmpty(error) && !emailMessage && value.length > 0);

    onError && onError(!isEmpty(error));
  };

  const keyboardNavigation = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (!['ArrowDown', 'ArrowUp', 'Escape', 'Enter'].includes(e.key)) {
      return;
    }

    showDomainSuggestion && e.preventDefault();

    if (e.key === 'ArrowDown') {
      showDomainSuggestion
        ? setCursor(prevState => (prevState < suggestedDomains.length - 1 ? prevState + 1 : 0))
        : setShowDomainSuggestion(false);
    }

    if (e.key === 'ArrowUp') {
      setCursor(prevState => (prevState > 0 ? prevState - 1 : suggestedDomains.length - 1));
    }

    if (e.key === 'Escape') {
      setShowDomainSuggestion(false);
    }

    if (e.key === 'Enter' && showDomainSuggestion) {
      setSelectedDomain(suggestedDomains[cursor]);
      setShowDomainSuggestion(false);
    }
  };

  const emailStatus = (emailSuggestion && 'caution') || ((fieldError || statusMessage) && 'error') || status;
  const inputStatus = (emailSuggestion && 'caution') || ((fieldError || statusMessage) && 'error') || status;

  const getInputBorderColor = (): string => {
    if (status === 'success') {
      return `1px solid ${colors.light.green.primary} !important`;
    }

    return emailStatus === 'error'
      ? `1px solid ${colors.light.warning.primary} !important`
      : `1px solid ${!isFocused && val === '' ? colors.gray['1300'] : colors.gray['1100']} !important`;
  };

  return (
    <MailCheck email={val}>
      {suggestion => {
        return (
          <EmailAutoSuggestWrapper onKeyDown={e => keyboardNavigation(e)} style={autoSuggestStyle}>
            <TextInputV2
              id="email"
              inputLabel={inputLabel}
              name="email"
              type="email"
              value={val}
              onBlur={e => handleOnTextChange(e, suggestion)}
              status={inputStatus}
              statusMessage={emailSuggestion || fieldError || statusMessage}
              customStyle={{ maxWidth: '452px', ...customStyle }}
              onChange={e => handleEmailAutoSuggest(e)}
              onFocus={(e: React.ChangeEvent<HTMLInputElement>) => handleEmailAutoSuggest(e)}
              onClick={() => handleOnClickVysionEvent()}
              customInputStyle={{
                ...{
                  border: getInputBorderColor(),
                },
                ...customInputStyle,
                ...(showDomainSuggestion && {
                  boxShadow: '0px 5px 6px 0px rgba(0,0,0,0.3)',
                  borderColor: 'rgba(0, 0, 0, 0.15) !important',
                }),
              }}
              customStyleLabelContainer={{ marginBottom: 8, textAlign: 'left', ...customStyleLabelContainer }}
              isLogin={isLogin}
              autoFocus={autoFocus}
              {...rest}
            />
            {showDomainSuggestion && (
              <AutoSuggestList>
                <li style={{ listStyle: 'none', borderTop: '1px dashed #ccc', margin: '0 12px', lineHeight: '5px' }}>
                  &nbsp;
                </li>
                {suggestedDomains.map((domain, idx) => {
                  return (
                    <AutoSuggestListItem
                      onMouseDown={() => setSelectedDomain(domain)}
                      key={domain}
                      className={cursor === idx ? 'active' : ''}
                    >
                      {domain}
                    </AutoSuggestListItem>
                  );
                })}
              </AutoSuggestList>
            )}
          </EmailAutoSuggestWrapper>
        );
      }}
    </MailCheck>
  );
};

export default EmailInputV2;
